import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async"; // SEO Layer
import { products } from "../../utils/products";
import ProductCard from "./ProductCard";
import { useLocation, useNavigate } from "react-router";
import {
  Filter,
  Home,
  ArrowRight,
  Layers,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import ProductCategoryCard from "./ProductCategoryCard";

const ProductPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categorySlug = queryParams.get("category");
  const isGeneralOverview = !categorySlug;

  const scrollTargetRef = useRef(null);

  // --- RECURSIVE LOOKUP (Functionality Preserved) ---
  const findCategoryRecursively = (items, slug) => {
    for (const item of items) {
      if (item.slug === slug) return item;
      if (item.subCategories) {
        const found = findCategoryRecursively(item.subCategories, slug);
        if (found) return found;
      }
    }
    return null;
  };

  const currentCategory = categorySlug
    ? findCategoryRecursively(products, categorySlug)
    : null;

  const [selectedCategory, setSelectedCategory] = useState(
    currentCategory ? currentCategory.category || currentCategory.name : "",
  );

  const [isVisible, setIsVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // SEO: Structured Data for the Product Registry
  const catalogSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: isGeneralOverview
      ? "Arc Engineering Product Registry"
      : `${selectedCategory} Catalog`,
    description: isGeneralOverview
      ? "Unified database for industrial generator systems and power infrastructure."
      : currentCategory?.description,
    url: `https://arcengltd.com/products${categorySlug ? `?category=${categorySlug}` : ""}`,
  };

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
    setCurrentPage(1);
    if (currentCategory) {
      setSelectedCategory(currentCategory.category || currentCategory.name);
    } else {
      setSelectedCategory("");
    }
  }, [categorySlug, currentCategory]);

  const hasSubCategories = currentCategory?.subCategories?.length > 0;
  const displayItems = hasSubCategories
    ? currentCategory.subCategories
    : currentCategory?.items || [];

  const totalItems = displayItems.length;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDisplayItems = displayItems.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const startRange = totalItems === 0 ? 0 : indexOfFirstItem + 1;
  const endRange = Math.min(indexOfLastItem, totalItems);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setTimeout(() => {
      if (scrollTargetRef.current) {
        scrollTargetRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 50);
  };

  const TopPagination = () => (
    <div className="flex items-center gap-6">
      <div className="hidden sm:flex items-center gap-2 border-r border-gray-100 pr-6">
        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-widest">
          Displaying
        </span>
        <span className="text-[11px] font-bold text-[#44444E] font-mono">
          {startRange}-{endRange} <span className="text-gray-300 mx-1">/</span>{" "}
          {totalItems}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-[10px] font-mono text-gray-400 uppercase tracking-widest hidden md:block">
          Page {currentPage} of {totalPages}
        </span>
        <div className="flex gap-1">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1.5 cursor-pointer rounded-lg border border-gray-100 text-[#44444E] disabled:opacity-20 hover:bg-gray-50 hover:border-[#BF092F] hover:text-[#BF092F] transition-all"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1.5 cursor-pointer rounded-lg border border-gray-100 text-[#44444E] disabled:opacity-20 hover:bg-gray-50 hover:border-[#BF092F] hover:text-[#BF092F] transition-all"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  const AllCategoriesOverview = () => (
    <div className="container mx-auto px-4 md:px-6 py-7 relative z-10">
      <div className="flex items-center gap-4 mb-8">
        <div className="h-8 w-1 bg-[#BF092F]" />
        <h2 className="text-sm text-[#44444E] uppercase tracking-[0.4em]">
          Product Categories
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        {products.map((category, idx) => (
          <ProductCategoryCard key={idx} category={category} />
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-[#44444E] font-sans selection:bg-[#BF092F] selection:text-white">
      <Helmet>
        <title>
          {isGeneralOverview
            ? "Industrial Product Registry | Arc Engineering"
            : `${selectedCategory} Systems | Arc Engineering`}
        </title>
        <meta
          name="description"
          content={
            isGeneralOverview
              ? "Browse our unified database for generator systems and critical power infrastructure."
              : currentCategory?.description
          }
        />
        <script type="application/ld+json">
          {JSON.stringify(catalogSchema)}
        </script>
      </Helmet>

      <div className="pt-22 px-2 md:px-2">
        <header className="shadow-xl relative h-[28vh] min-h-[300px] w-full flex items-center bg-[#44444E] overflow-hidden rounded-2xl">
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent z-10" />
          <div className="container mx-auto px-4 md:px-6 relative z-20">
            <nav className="hidden md:flex items-center flex-wrap gap-3 mb-6">
              <button
                onClick={() => navigate("/")}
                className="cursor-pointer group flex items-center gap-1 text-white/50 hover:text-white transition-colors"
              >
                <Home size={14} />
                <span className="text-[10px] md:text-xs tracking-widest uppercase">
                  Home
                </span>
              </button>
              <span className="text-white/20 text-xs font-mono">{">"}</span>
              <button
                onClick={() => navigate("/products")}
                className={`cursor-pointer text-[10px] md:text-xs tracking-widest uppercase transition-all duration-300 ${isGeneralOverview ? "bg-[#BF092F] text-white px-4 py-1.5 rounded-2xl shadow-lg" : "text-white/50 hover:text-white"}`}
              >
                Product Categories
              </button>
              {!isGeneralOverview && currentCategory && (
                <>
                  <span className="text-white/20 text-xs font-mono">{">"}</span>
                  <button className="text-[10px] md:text-xs tracking-widest uppercase bg-[#BF092F] text-white px-4 py-1.5 rounded-2xl shadow-lg">
                    {currentCategory.category || currentCategory.name}
                  </button>
                </>
              )}
            </nav>
            <div>
              <h1
                className={`font-semibold text-3xl md:text-5xl lg:text-5xl text-white leading-[1.1] tracking-[-0.02em] max-w-4xl transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              >
                {isGeneralOverview
                  ? "Product"
                  : currentCategory?.category || currentCategory?.name}{" "}
                <span className="text-[#BF092F]">
                  {isGeneralOverview ? "Registry" : "Catalog"}
                </span>
              </h1>
              <p className="text-white/60 text-lg md:text-xl tracking-wide leading-relaxed mt-4 max-w-3xl">
                {isGeneralOverview
                  ? "Accessing unified database for generator systems."
                  : currentCategory?.description}
              </p>
            </div>
          </div>
        </header>
      </div>

      <main className="container mx-auto -translate-y-12 relative z-30">
        {isGeneralOverview ? (
          <div className="pt-12">
            <AllCategoriesOverview />
          </div>
        ) : (
          <div className="pt-20 px-4 flex flex-col lg:grid lg:grid-cols-12 gap-8 items-stretch">
            <aside className="lg:col-span-3 space-y-8 h-full">
              <div className="rounded-2xl bg-[#44444E] shadow-2xl border-t-4 border-[#BF092F] sticky top-[20vh] overflow-hidden transition-all duration-500">
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-8">
                    <Filter size={16} className="text-[#BF092F]" />
                    <h2 className="text-[12px] text-white tracking-[0.4em] uppercase">
                      Refine Registry
                    </h2>
                  </div>

                  {/* MOBILE DROPDOWN: Fixed for long category names */}
                  <div className="lg:hidden relative">
                    <select
                      value={categorySlug || ""}
                      onChange={(e) =>
                        navigate(`/products?category=${e.target.value}`)
                      }
                      className="w-full bg-white/5 border border-white/10 text-white text-[12px] uppercase tracking-[0.2em] px-4 py-4 pr-10 rounded-xl cursor-pointer focus:outline-none focus:border-[#BF092F] appearance-none truncate"
                      style={{ textOverflow: "ellipsis" }}
                    >
                      <option value="" className="bg-[#44444E]">
                        All Products
                      </option>
                      {products.map((cat) => (
                        <React.Fragment key={cat.slug}>
                          <option
                            value={cat.slug}
                            className="bg-[#44444E] font-bold"
                          >
                            {cat.category || cat.name}
                          </option>

                          {cat.subCategories?.map((sub) => (
                            <option
                              key={sub.slug}
                              value={sub.slug}
                              className="bg-[#44444E] text-white/60"
                            >
                              &nbsp;&nbsp;— {sub.name}
                            </option>
                          ))}
                        </React.Fragment>
                      ))}
                    </select>

                    {/* The Visual Toggle: Absolute positioned so it never moves */}
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#BF092F]">
                      <ChevronRight size={16} className="rotate-90" />
                    </div>
                  </div>

                  {/* DESKTOP SIDEBAR: Preserves your recursive nesting UI */}
                  <ul className="hidden lg:block space-y-2">
                    {products.map((cat, idx) => {
                      const catName = cat.category || cat.name;
                      const isParentActive =
                        selectedCategory === catName ||
                        cat.subCategories?.some(
                          (sub) => sub.name === selectedCategory,
                        );

                      return (
                        <li key={idx} className="space-y-2">
                          <div
                            onClick={() =>
                              navigate(`/products?category=${cat.slug}`)
                            }
                            className={`cursor-pointer px-4 py-4 rounded-xl text-[12px] uppercase tracking-[0.2em] transition-all flex items-center justify-between group ${
                              isParentActive
                                ? "bg-white/10 text-white border-l-4 border-[#BF092F]"
                                : "text-white/40 hover:text-white hover:bg-white/5"
                            }`}
                          >
                            {catName}
                            <ChevronRight
                              size={14}
                              className={`transition-transform duration-300 ${
                                isParentActive
                                  ? "text-[#BF092F] translate-x-1"
                                  : "opacity-0 group-hover:opacity-100"
                              }`}
                            />
                          </div>

                          {isParentActive && cat.subCategories && (
                            <ul className="ml-4 pl-4 border-l border-white/5 space-y-1 pb-2">
                              {cat.subCategories.map((sub, sIdx) => {
                                const isSubActive = categorySlug === sub.slug;
                                return (
                                  <li
                                    key={sIdx}
                                    onClick={() =>
                                      navigate(`/products?category=${sub.slug}`)
                                    }
                                    className={`cursor-pointer px-4 py-2 rounded-lg text-[10px] uppercase tracking-[0.15em] transition-all flex items-center justify-between group ${
                                      isSubActive
                                        ? "text-[#BF092F] font-bold"
                                        : "text-white/30 hover:text-white/60"
                                    }`}
                                  >
                                    {sub.name}
                                    {isSubActive && (
                                      <div className="w-1 h-1 rounded-full bg-[#BF092F]" />
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </aside>

            <section className="lg:col-span-9">
              <div
                ref={scrollTargetRef}
                className="bg-white p-8 md:p-12 rounded-2xl shadow-xl border border-gray-100 min-h-[600px] flex flex-col scroll-mt-32"
              >
                <div className="flex items-center justify-between mb-12 border-b border-gray-100 pb-8">
                  <h2 className="tracking-widest border-l-4 border-[#BF092F] pl-4 text-[#44444E] uppercase text-sm font-bold">
                    {hasSubCategories
                      ? "Available Categories"
                      : "Available Items"}
                  </h2>
                  <TopPagination />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 flex-grow">
                  {currentDisplayItems.map((item, idx) => (
                    <div
                      key={idx}
                      className="transition-all duration-500 hover:-translate-y-2"
                    >
                      {hasSubCategories ? (
                        <ProductCategoryCard
                          category={item}
                          onClick={() =>
                            navigate(`/products?category=${item.slug}`)
                          }
                        />
                      ) : (
                        <ProductCard
                          product={item}
                          categorySlug={categorySlug}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-16 pt-8 border-t border-gray-100 flex flex-col items-center gap-6">
                  <div className="text-[11px] font-mono text-gray-400 uppercase tracking-widest">
                    Showing {startRange} to {endRange} of {totalItems} Items
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-3 cursor-pointer rounded-xl border border-gray-200 text-[#44444E] disabled:opacity-30 hover:border-[#BF092F] transition-all"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <div className="flex items-center gap-2">
                      {[...Array(totalPages)].map((_, i) => (
                        <button
                          key={i}
                          onClick={() => handlePageChange(i + 1)}
                          className={`cursor-pointer w-10 h-10 rounded-xl text-xs font-bold transition-all ${currentPage === i + 1 ? "bg-[#BF092F] text-white shadow-lg" : "bg-gray-50 text-gray-400 hover:bg-gray-100"}`}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-3 cursor-pointer rounded-xl border border-gray-200 text-[#44444E] disabled:opacity-30 hover:border-[#BF092F] transition-all"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </main>
      <div className="fixed inset-0 -z-10 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
    </div>
  );
};

export default ProductPage;
